import { createPortfolio, getPortfolios } from "@/pages/PortfolioBuilder/api";
import { emptyNotifications } from "@/types/notifications";
import type { Portfolio } from "@/types/Portfolio";
import { makeNameClaimer } from "./concierge/claimName";
import { ownPortfolioTerms } from "./portfolioEntries";
import type { TemplateWidgetSpec } from "./templates/types";

const DEFAULT_NAME = "My Portfolio";

/** Section the dedicated instance filters on — the ticker-watchlist filter. */
const FILTER_SECTION = "ticker-watchlists-filter";

export type OwnPortfolioResult = {
  id: number;
  name: string;
  /**
   * A headlines instance narrowed to just these names, to append to the tab
   * being built. Separate from the primary wire, which only ever highlights.
   */
  widget: TemplateWidgetSpec;
};

/**
 * Turns the entries from step 1 into a real watchlist.
 *
 * Shared by both routes: the template path builds it before applying a layout,
 * and the concierge path creates it before the plan so the LLM's own lists sit
 * alongside it rather than duplicating it.
 *
 * Named through `makeNameClaimer` against the user's existing lists, so
 * someone onboarding a second time — or who already has a "My Portfolio" —
 * gets "My Portfolio 2" rather than a second indistinguishable row in the
 * filter panel.
 *
 * Returns null when there is nothing to make, so callers can treat "no
 * tickers" and "creation failed" the same way: carry on without it. The
 * dashboard is the thing the user asked for and must not fail over this.
 */
export async function createOwnPortfolio(
  tickers: string[]
): Promise<OwnPortfolioResult | null> {
  const entered = tickers.map((t) => t.trim()).filter(Boolean);
  if (entered.length === 0) return null;

  try {
    /**
     * The rule the watchlist builder uses: symbols as written, and anything
     * else a keyword as typed (portfolioEntries.ts). Nothing is looked up, so
     * "Apple" stays the word rather than turning into whichever ticker a tag
     * lookup offers — the step has already asked about anything unclear.
     */
    const { tickers: symbols, keywords } = ownPortfolioTerms(entered);
    const existing = await getPortfolios().catch(() => [] as Portfolio[]);
    const claim = makeNameClaimer(existing.map((p) => p.name));
    const name = claim(DEFAULT_NAME);
    // The server allocates the id and ignores the one sent, so the widget
    // filter below has to use the id that comes back — the one sent pointed
    // the new panel at some other list, or at none.
    const { id } = await createPortfolio({
      id: 0,
      name,
      tickers: symbols,
      keywords,
      color: "#00AEEF",
      notifications: emptyNotifications(),
      customizations: { includeRelatedHeadlines: false },
      owner_type: "USER",
    });

    return {
      id,
      name,
      widget: {
        id: "NewsquawkHeadlines-mine",
        size: { w: 5, h: 5 },
        filter: { [FILTER_SECTION]: [String(id)] },
      },
    };
  } catch (error) {
    console.error("[onboarding] failed to create own portfolio:", error);
    return null;
  }
}
